import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  NotificationAggregateItem,
  NotificationListResponse,
  NotificationMarkViewedBody,
} from '@magimp/shared';
import { markNotificationViewed } from '../../../api/notifications.js';
import { NOTIFICATIONS_LIST_QK } from './useNotificationsList.js';

/**
 * @aiPurpose mark a single aggregate viewed by id (bell-row click path).
 *   Optimistically clears `unreadCount` and writes `viewedAt`, then
 *   reconciles from the server response. On error invalidates the list
 *   so the panel re-reads.
 * @aiInvariant the by-id branch is the only path used from the bell UI;
 *   tab-mount auto-view (by-target) lands in #1072.
 */
export function useMarkNotificationViewed() {
  const queryClient = useQueryClient();
  return useMutation<NotificationAggregateItem, Error, NotificationMarkViewedBody>({
    mutationFn: (body) => markNotificationViewed(body),
    onMutate: async (body) => {
      if (body.by !== 'id') return;
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_LIST_QK });
      const prev = queryClient.getQueryData<NotificationListResponse>(NOTIFICATIONS_LIST_QK);
      if (prev) {
        const nowIso = new Date().toISOString();
        const next = prev.map((row) =>
          row.id === body.id
            ? { ...row, unreadCount: 0, firstUnreadAt: null, viewedAt: nowIso }
            : row,
        );
        queryClient.setQueryData<NotificationListResponse>(NOTIFICATIONS_LIST_QK, next);
      }
      return { prev };
    },
    onError: (_err, _body, context) => {
      const prev = (context as { prev?: NotificationListResponse } | undefined)?.prev;
      if (prev) {
        queryClient.setQueryData<NotificationListResponse>(NOTIFICATIONS_LIST_QK, prev);
      } else {
        void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_LIST_QK });
      }
    },
    onSuccess: (next) => {
      const prev = queryClient.getQueryData<NotificationListResponse>(NOTIFICATIONS_LIST_QK);
      if (prev) {
        const reconciled = prev.map((row) => (row.id === next.id ? next : row));
        queryClient.setQueryData<NotificationListResponse>(NOTIFICATIONS_LIST_QK, reconciled);
      }
    },
  });
}
